import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Sparkles, Loader2, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function Pricing() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activePlan, setActivePlan] = useState(null);
  const [orgStatus, setOrgStatus] = useState(null);

  useEffect(() => {
    api.get("/billing/plans").then((r) => setPlans(r.data)).catch(() => {});
    if (user) {
      api.get("/orgs/me").then((r) => {
        setActivePlan(r.data.plan);
        setOrgStatus(r.data.status);
      }).catch(() => {});
    }
  }, [user]);

  const choose = async (planId) => {
    if (!user) {
      nav(`/signup-org?plan=${planId}`);
      return;
    }
    if (user.role !== "admin") {
      toast.error("Endast organisationens ägare kan välja prenumeration");
      return;
    }
    setLoading(planId);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte starta betalning");
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 animate-fade-in-up">
      <div className="text-center mb-12">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-700 mb-3">
          Prenumeration · SaaS för kommuner
        </div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-900" style={{ fontFamily: "Outfit" }}>
          Välj plan som passar er kommun
        </h1>
        <p className="mt-4 text-base text-zinc-600 max-w-2xl mx-auto">
          Alla planer inkluderar svensk hosting, GDPR-trygg lagring och kontinuerliga uppdateringar.
          Byt plan eller säg upp närsomhelst.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {plans.map((p) => {
          const isHighlight = p.highlight;
          const isCurrent = activePlan === p.id && orgStatus === "active";
          return (
            <div
              key={p.id}
              data-testid={`plan-card-${p.id}`}
              className={`relative bg-white border rounded-sm p-7 flex flex-col ${
                isHighlight ? "border-blue-700 shadow-lg ring-1 ring-blue-100" : "border-zinc-200"
              }`}
            >
              {isHighlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-blue-700 text-white text-[10px] uppercase tracking-wider font-semibold rounded-sm">
                  Mest valda
                </span>
              )}
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">{p.name}</div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-4xl font-semibold text-zinc-900" style={{ fontFamily: "Outfit" }}>
                  {Number(p.price_sek).toLocaleString("sv-SE")}
                </span>
                <span className="text-sm text-zinc-500">kr/mån</span>
              </div>
              <p className="mt-2 text-sm text-zinc-600">{p.tagline}</p>

              <ul className="mt-6 space-y-2.5 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-zinc-700">
                    <Check className="w-4 h-4 text-blue-700 mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => choose(p.id)}
                disabled={loading === p.id || isCurrent}
                data-testid={`choose-plan-${p.id}`}
                className={`mt-7 w-full px-4 py-3 text-sm font-medium rounded-sm inline-flex items-center justify-center gap-2 transition disabled:opacity-60 ${
                  isCurrent
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-default"
                    : isHighlight
                    ? "bg-blue-700 hover:bg-blue-800 text-white"
                    : "bg-zinc-900 hover:bg-zinc-800 text-white"
                }`}
              >
                {loading === p.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : isCurrent ? (
                  <><Check className="w-4 h-4" /> Aktiv plan</>
                ) : user ? (
                  <>Uppgradera till {p.name} <ArrowRight className="w-4 h-4" /></>
                ) : (
                  <>Kom igång med {p.name} <ArrowRight className="w-4 h-4" /></>
                )}
              </button>
            </div>
          );
        })}
      </div>

      <div className="mt-12 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-sm text-sm text-blue-900">
          <Sparkles className="w-4 h-4" />
          14 dagars gratis trial · Inga uppsägningstider · Svenskt support
        </div>
      </div>
    </div>
  );
}
